import { mergeClassNames } from '@/utils/mergeClassNames';
import { Icon, type IconName } from '@zapier/zinnia-icons';
import { Context, useContext } from 'react';
import {
  Button,
  TagProps as ReactAriaTagProps,
  Tag,
  TagGroupContext,
  useContextProps,
} from 'react-aria-components';
import { TagGroupContextValue } from './FilterChipList';

import styles from './FilterChip.module.scss';

// Omits `children` because otherwise the JSDoc comment from RAC gets combined with ours.
export type FilterChipProps = Omit<ReactAriaTagProps, 'children'> & {
  /** The contents of `FilterChip`. Should be `FilterChipIcon`. A function may be provided to alter the children based on the chip's state. */
  children?: ReactAriaTagProps['children'];
  /** An optional label to render next to the title. */
  label?: string;
  /** Whether to display a checkmark icon on the left side of the `FilterChip` whenever the chip is selected. */
  showsCheckmarkWhenSelected?: boolean;
  /**
   * The size of the `FilterChip`. Use only to override the size for an individual `FilterChip`. Otherwise, pass `size` to `FilterChipList`.
   * @default 'small'
   */
  size?: 'compact' | 'small' | 'medium';
  /** The title of the `FilterChip`. */
  title: string;
  /**
   * The visual style of the `FilterChip`. Use only to override the variant for an individual `FilterChip`. Otherwise, pass `variant` to `FilterChipList`.
   * @default 'primary'
   */
  variant?: 'primary' | 'neutral';
};

/**
 * A single chip that can be used to allow users to filter options. Must be rendered inside of `FilterChipList`.
 */
export const FilterChip = ({
  children,
  className,
  id,
  isDisabled,
  label,
  showsCheckmarkWhenSelected = false,
  style,
  title,
  ...otherProps
}: FilterChipProps) => {
  const [contextProps] = useContextProps(otherProps, null, TagGroupContext);
  const { size = 'small', variant = 'primary', ...props } = contextProps;

  const tagGroupContext = useContext(
    TagGroupContext as Context<TagGroupContextValue>
  );

  return (
    <Tag
      {...props}
      className={(renderProps) =>
        mergeClassNames([styles.root, className], renderProps)
      }
      data-selectable={tagGroupContext?.selectionMode ?? undefined}
      data-size={size}
      data-variant={variant}
      data-zds
      id={id}
      isDisabled={isDisabled}
      style={style}
      textValue={label ? `${title} ${label}` : title}
    >
      {(tagRenderProps) => (
        <>
          {tagRenderProps.isSelected && showsCheckmarkWhenSelected && (
            <Icon className={styles.checkmarkIcon} name="formCheck" />
          )}

          {typeof children === 'function' ? children(tagRenderProps) : children}

          {title && (
            <div className={styles.titleLabelWrapper} data-zds>
              <span
                className={styles.title}
                data-zds
                data-testid="filter-chip-title"
              >
                {title}
              </span>
              {!!label && (
                <span
                  className={styles.label}
                  data-zds
                  data-testid="filter-chip-label"
                >
                  {label}
                </span>
              )}
            </div>
          )}

          {tagRenderProps.allowsRemoving && (
            <Button
              className={styles.removalButton}
              data-removal-button
              data-zds
              slot="remove"
            >
              <Icon color="StrokeStronger" name="formX" />
            </Button>
          )}
        </>
      )}
    </Tag>
  );
};

export type FilterChipIconProps = {
  /** The name of icon to display. If the name is invalid, a square will be rendered instead. */
  name: IconName;
};

/** An icon whose styles will dynamically adjust when passed as a child to `FilterChip`. */
export const FilterChipIcon = ({ name }: FilterChipIconProps) => (
  <Icon
    shouldAnimateFill={false}
    className={styles.filterChipIcon}
    name={name}
  />
);
